/**
 * Network endpoint derivation — single source of truth.
 *
 * OverworldScene talks to the Colyseus realtime server (websocket) and,
 * for auxiliary HTTP-only surfaces, to the app HTTP API. Each endpoint can
 * be overridden at build time via Vite env vars; otherwise we derive a
 * sensible default from the page origin so dev / staging / prod all "just
 * work" without per-environment configuration.
 *
 * Production:  served behind the same origin as the game client; both
 *              endpoints share the page host with TLS.
 * Local dev:   client served by Vite on :5173, Colyseus on :2567,
 *              HTTP API on :3000 — same hostname, different ports.
 */

interface ViteEnv {
  env?: {
    VITE_COLYSEUS_URL?: string;
    VITE_API_URL?: string;
  };
}

const env = (import.meta as unknown as ViteEnv).env;

/** Colyseus websocket endpoint. wss:// in prod (same origin), ws://host:2567 in dev. */
export const COLYSEUS_URL: string =
  env?.VITE_COLYSEUS_URL ||
  (window.location.protocol === 'https:'
    ? `wss://${window.location.host}/ws`
    : `ws://${window.location.hostname}:2567`);

/** HTTP API endpoint (auxiliary REST surfaces — not used by the realtime loop). */
export const API_URL: string =
  env?.VITE_API_URL ||
  (window.location.protocol === 'https:'
    ? `${window.location.origin}/api`
    : `${window.location.protocol}//${window.location.hostname}:3000`);
